// Editorial (magazine) view — renders a featured lead card + side cards + grid.
// Matches design/project/scripts/feed.jsx EditorialView, EdFeature, EdSideCard, EdCard.
import { useState } from "react";
import { isSafeHttpUrl } from "@/lib/url";
import type { EmptyState, FeedPostItem, PostType } from "../api";
import { Icon } from "../design-atoms";
import { CoverArt } from "./CoverArt";
import { FeedEmptyState } from "./FeedEmptyState";

const TYPE_LABEL: Record<PostType, string> = {
  project: "Project",
  post: "Post",
};

// YouTube-style comment count for editorial bylines — rendered only when the
// card has at least one comment (0 stays hidden). Kept read-only here; the
// discussion opens on the detail page.
function EdComments({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className="hub-ed-comments"
      aria-label={`${count} comment${count === 1 ? "" : "s"}`}
    >
      <Icon.comment size={12} /> {count}
    </span>
  );
}

// Cover: the post's unfurled OpenGraph image when present, otherwise generated
// CoverArt. If the OG image URL 404s / fails to load, we fall back to CoverArt
// so a broken external image never leaves an empty box.
function Cover({ post }: { post: FeedPostItem }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = isSafeHttpUrl(post.cover_image_url) && !imgFailed;
  if (showImage) {
    return (
      <img
        className="hub-ed-cover-img"
        src={post.cover_image_url as string}
        alt=""
        aria-hidden="true"
        loading="lazy"
        onError={() => setImgFailed(true)}
      />
    );
  }
  return <CoverArt type={post.type} division={post.division} seed={post.id} />;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ----------------------------------------------------------------------------
// Feature card (lead — largest; first post)
// ----------------------------------------------------------------------------

// Shared interaction props that turn a card <article> into an activatable
// button (click + Enter/Space) opening the post. Mirrors design feed.jsx's
// `activate()` helper applied to editorial cards.
function cardActivation(onOpen: ((id: string) => void) | undefined, id: string) {
  if (!onOpen) return {};
  return {
    role: "button" as const,
    tabIndex: 0,
    style: { cursor: "pointer" as const },
    onClick: () => onOpen(id),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen(id);
      }
    },
  };
}

function EdFeature({ post, onOpen }: { post: FeedPostItem; onOpen?: (id: string) => void }) {
  const typeLabel = TYPE_LABEL[post.type as PostType] ?? post.type;
  return (
    <article
      className="hub-ed-feature"
      data-testid="ed-feature"
      aria-label={post.title}
      {...cardActivation(onOpen, post.id)}
    >
      <div className="hub-ed-hero" aria-hidden="true">
        <Cover post={post} />
        <span className="hub-ed-hero__division">{post.division}</span>
      </div>
      <div className="hub-ed-feature__body">
        <div className="hub-ed-kicker">
          {post.pinned && (
            <span className="hub-ed-pinned" aria-label="Pinned">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              {" Pinned · "}
            </span>
          )}
          {typeLabel}
        </div>
        <h2 className="hub-ed-feature__title">{post.title}</h2>
        <p className="hub-ed-dek">{post.excerpt}</p>
        <div className="hub-ed-byline">
          <span className="hub-ed-byline__division">{post.division}</span>
          <span className="hub-ed-byline__time">{timeAgo(post.created_at)}</span>
          <span className="hub-ed-votes" aria-label={`${post.vote_count} votes`}>
            ▲ {post.vote_count}
          </span>
          <EdComments count={post.comment_count} />
        </div>
      </div>
    </article>
  );
}

// ----------------------------------------------------------------------------
// Side card (compact secondary card shown next to the feature)
// ----------------------------------------------------------------------------

function EdSideCard({ post, onOpen }: { post: FeedPostItem; onOpen?: (id: string) => void }) {
  const typeLabel = TYPE_LABEL[post.type as PostType] ?? post.type;
  return (
    <article
      className="hub-ed-sidecard"
      data-testid="ed-sidecard"
      aria-label={post.title}
      {...cardActivation(onOpen, post.id)}
    >
      <div className="hub-ed-kicker">{typeLabel}</div>
      <h3 className="hub-ed-sidecard__title">{post.title}</h3>
      <p className="hub-ed-dek">{post.excerpt}</p>
      <div className="hub-ed-sidecard__meta">
        <span className="hub-ed-byline__division">{post.division}</span>
        <span> · </span>
        <span className="hub-ed-byline__time">{timeAgo(post.created_at)}</span>
        <EdComments count={post.comment_count} />
      </div>
    </article>
  );
}

// ----------------------------------------------------------------------------
// Grid card (used for remaining posts after lead + side cards)
// ----------------------------------------------------------------------------

function EdCard({ post, onOpen }: { post: FeedPostItem; onOpen?: (id: string) => void }) {
  const typeLabel = TYPE_LABEL[post.type as PostType] ?? post.type;
  return (
    <article
      className="hub-ed-card"
      data-testid="ed-card"
      aria-label={post.title}
      {...cardActivation(onOpen, post.id)}
    >
      <div className="hub-ed-thumb" aria-hidden="true">
        <Cover post={post} />
      </div>
      <div className="hub-ed-kicker hub-ed-card__kicker">{typeLabel}</div>
      <h3 className="hub-ed-card__title">{post.title}</h3>
      <p className="hub-ed-dek">{post.excerpt}</p>
      <div className="hub-ed-byline">
        <span className="hub-ed-byline__division">{post.division}</span>
        <span> · </span>
        <span className="hub-ed-byline__time">{timeAgo(post.created_at)}</span>
        <span className="hub-ed-votes" aria-label={`${post.vote_count} votes`}>
          ▲ {post.vote_count}
        </span>
        <EdComments count={post.comment_count} />
      </div>
    </article>
  );
}

// ----------------------------------------------------------------------------
// Editorial container
// ----------------------------------------------------------------------------

interface Props {
  posts: FeedPostItem[];
  emptyState: EmptyState | null;
  /** Called when an editorial card is activated — opens the post detail. */
  onOpen?: (id: string) => void;
}

export function EditorialView({ posts, emptyState, onOpen }: Props) {
  if (!posts.length) {
    return <FeedEmptyState emptyState={emptyState} />;
  }

  const [lead, s1, s2, ...rest] = posts;

  return (
    <div className="hub-editorial" data-testid="editorial-view">
      {/* Lead section: featured article + side cards */}
      <div className="hub-ed-lead">
        <EdFeature post={lead} onOpen={onOpen} />
        <div className="hub-ed-side">
          {[s1, s2].filter(Boolean).map((p) => (
            <EdSideCard key={p!.id} post={p!} onOpen={onOpen} />
          ))}
        </div>
      </div>

      {/* Remaining posts grid */}
      {rest.length > 0 && (
        <div className="hub-ed-more">
          <div className="hub-ed-section-title">More from the community</div>
          <div className="hub-ed-grid">
            {rest.map((p) => (
              <EdCard key={p.id} post={p} onOpen={onOpen} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
